import React, { useState, useEffect, useRef, useCallback } from "react";
import { applyInputErrorStyling, removeInputErrorStyling } from "js/utils/dynamicStyling";
import PrivacyPolicy from "../components/PrivacyPolicy";
import LdapLoginButton from "./components/LdapLoginButton";
import ClasslinkLoginButton from "./components/ClasslinkLoginButton";
import GoogleLoginButton from "./components/GoogleLoginButton";
import AzureLoginButton from "./components/AzureLoginButton";
import { useThemeStore } from "stores/v2/themeStore";
import { useUserSessionStore } from "stores/v2/userSessionStore";
import { Notice } from "@/components/Notice";
import { Box, Spinner } from "@chakra-ui/react";
import { LoginOptionsResponse } from "js/api/authApi";
import * as authApi from "js/api/authApi";
import { datadogLogs } from "@datadog/browser-logs";
import { useNavigate, useParams, useLocation } from "react-router-dom";

const DeviceLogin: React.FC = () => {
	// Router hooks
	const navigate = useNavigate();
	const params = useParams();
	const location = useLocation();

	// State management with useState hooks
	const [deviceid, setDeviceid] = useState<string | null>(null);
	const [loginFailed, setLoginFailed] = useState<boolean>(useUserSessionStore.getState().failedLogin());
	const [googleLoginEnabled, setGoogleLoginEnabled] = useState<boolean>(false);
	const [azureLoginEnabled, setAzureLoginEnabled] = useState<boolean>(false);
	const [classlinkLoginEnabled, setClasslinkLoginEnabled] = useState<boolean>(false);
	const [passwordLoginEnabled, setPasswordLoginEnabled] = useState<boolean>(false);
	const [passwordLoggingIn, setPasswordLoggingIn] = useState<boolean>(false);
	const isAuthenticated = useUserSessionStore.getState().isAuthenticated();

	// Refs for DOM elements
	const usernameInputElement = useRef<HTMLInputElement | null>(null);
	const passwordInputElement = useRef<HTMLInputElement | null>(null);

	// Derived state
	const showBookmarkPageNotice = location.state?.fromSchoolSelectPage ?? false;

	const handleLdapUsernameChange = useCallback((): void => {
		removeInputErrorStyling(usernameInputElement.current, passwordInputElement.current);
	}, []);

	const handleLdapPasswordChange = useCallback((): void => {
		removeInputErrorStyling(usernameInputElement.current, passwordInputElement.current);
	}, []);

	const ldapLoginValid = useCallback((username: string, password: string): boolean => {
		// clear any previous error styling
		removeInputErrorStyling(usernameInputElement.current, passwordInputElement.current);

		const errorElements: (HTMLInputElement | null)[] = [];
		if (username.length === 0) {
			errorElements.push(usernameInputElement.current);
		}
		if (password.length === 0) {
			errorElements.push(passwordInputElement.current);
		}
		applyInputErrorStyling(...errorElements);

		const validationPassed = errorElements.length === 0;
		return validationPassed;
	}, []);

	const handleLdapSubmit = useCallback(
		(username: string, password: string): void => {
			removeInputErrorStyling(usernameInputElement.current, passwordInputElement.current);

			if (ldapLoginValid(username, password) && deviceid) {
				setPasswordLoggingIn(true);
				useUserSessionStore.getState().authenticate({
					deviceid: deviceid,
					username: username,
					password: password,
				});
			}
		},
		[deviceid, ldapLoginValid]
	);

	// useEffect for componentDidMount equivalent
	useEffect(() => {
		if (isAuthenticated) {
			navigate("/");
		}

		authApi
			.getLoginMethods(params.tenant_key as string)
			.then((res: LoginOptionsResponse) => {
				setDeviceid(res.applianceId);
				sessionStorage.setItem("LOGIN_DEVICEID", res.applianceId);
				setGoogleLoginEnabled(res.loginOptions.google);
				setAzureLoginEnabled(res.loginOptions.azure);
				setClasslinkLoginEnabled(res.loginOptions.classlink);
				setPasswordLoginEnabled(res.loginOptions.password);
			})
			.catch((error) => {
				datadogLogs.logger.error("Error fetching login methods", error);
			});
	}, [params.tenant_key, isAuthenticated, navigate]);

	// Subscribe to user session store changes
	useEffect(() => {
		const unsubscribe = useUserSessionStore.subscribe(() => {
			const currentIsAuthenticated = useUserSessionStore.getState().isAuthenticated();
			const currentLoginFailed = useUserSessionStore.getState().failedLogin();

			if (currentIsAuthenticated) {
				navigate("/");
			}

			if (currentLoginFailed !== loginFailed) {
				setLoginFailed(currentLoginFailed);
			}
		});

		return unsubscribe;
	}, [navigate, loginFailed]);

	// useEffect for componentDidUpdate equivalent
	useEffect(() => {
		if (loginFailed) {
			setPasswordLoggingIn(false);
			setLoginFailed(false);
			applyInputErrorStyling(usernameInputElement.current, passwordInputElement.current);
		}
	}, [loginFailed]);

	// Render functions
	const renderLoginFormOptions = useCallback((): JSX.Element => {
		return (
			<div>
				{googleLoginEnabled && deviceid && <GoogleLoginButton deviceId={deviceid} />}
				{azureLoginEnabled && deviceid && <AzureLoginButton deviceId={deviceid} />}
				{classlinkLoginEnabled && deviceid && <ClasslinkLoginButton deviceId={deviceid} />}
				{/* Render 'or' separator if any of the above third party sign in buttons are enabled */}
				{(googleLoginEnabled || azureLoginEnabled || classlinkLoginEnabled) && passwordLoginEnabled && (
					<div className="login-separator">
						<div className="login-separator-line" />
						<div className="login-separator-text">or</div>
						<div className="login-separator-line" />
					</div>
				)}
				{passwordLoginEnabled && deviceid && (
					<LdapLoginButton
						usernameRef={(el: HTMLInputElement | null) => {
							usernameInputElement.current = el;
						}}
						passwordRef={(el: HTMLInputElement | null) => {
							passwordInputElement.current = el;
						}}
						handleSubmit={handleLdapSubmit}
						loginInProgress={passwordLoggingIn}
						onUsernameChange={handleLdapUsernameChange}
						onPasswordChange={handleLdapPasswordChange}
					/>
				)}
			</div>
		);
	}, [
		googleLoginEnabled,
		azureLoginEnabled,
		classlinkLoginEnabled,
		deviceid,
		handleLdapSubmit,
		passwordLoggingIn,
		handleLdapUsernameChange,
		handleLdapPasswordChange,
		passwordLoginEnabled,
	]);

	return (
		<div id="login-main-content">
			<div className="background">
				<div className="background-left" />
				<div className="background-right" />
			</div>
			<div className="flex-row-center">
				<div className="login-content">
					<div className={useThemeStore.getState().theme === "smoothwall" ? "login-logo-smoothwall" : "login-logo"} />
					{useThemeStore.getState().theme === "linewize" && <h1>Classroom Manager</h1>}
					<h3>Teacher Portal</h3>
					{showBookmarkPageNotice && (
						<Notice icon="notice" status="error" textAlign="left">
							<Box fontWeight="bold" mb={2}>
								Heads Up! Save This Page
							</Box>
							<Box>
								This page is your school's unique Classroom Manager login page. Bookmark this page so you can easily return here — the
								school selection step from the previous page will be removed soon.
							</Box>
						</Notice>
					)}
					<div className="login-content-inner">
						{deviceid ? (
							renderLoginFormOptions()
						) : (
							<Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
								<Spinner size="xl" />
							</Box>
						)}
					</div>
				</div>
			</div>
			<PrivacyPolicy />
		</div>
	);
};

export default DeviceLogin;
